/**
 * Create/edit form for release updates (admin).
 */

import { useMediaQuery } from 'react-responsive';

import {
  Button,
  Div,
  FeedbackFormInput,
  H1,
  P,
} from '../../../styles/styles';

const LANGUAGES = ['en', 'ru', 'sr'];

export const UPDATE_CREATE_FORM_ID = 'update-create-form';

export function updateEditFormId(updateId) {
  return `update-edit-form-${updateId}`;
}

export default function AdminUpdateFormPanel({
  title,
  formId,
  formData,
  setFormData,
  isEdit,
  onCancel,
  onSubmit,
  saving,
}) {
  const isDesktop = useMediaQuery({ minWidth: '800px' });
  const busy = saving;

  const patchLang = (field, lang, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: { ...prev[field], [lang]: value },
    }));
  };

  const setVisibilityFlag = (field, checked) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: checked };
      if (field === 'webOnly' && checked) next.mobileOnly = false;
      if (field === 'mobileOnly' && checked) next.webOnly = false;
      return next;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit();
  };

  return (
    <Div
      width="100%"
      padding="20px"
      borderRadius="10px"
      backWhite
      grayBorder
      style={{ marginTop: 12, boxSizing: 'border-box' }}
    >
      <H1 fontSize="20px" margin="0 0 15px 0">
        {title}
      </H1>

      <form id={formId} onSubmit={handleSubmit} style={{ width: '100%' }}>
        <Div gap="15px" width="100%">
          <Div width="100%" alignStart gap="8px">
            <P fontSize="14px" margin="0 0 5px 0" textAlign="left">
              Published date
            </P>
            <FeedbackFormInput
              type="date"
              value={formData.publishedAt}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, publishedAt: e.target.value }))
              }
              required
              style={{ padding: '3px 5px', border: '1px gray solid', maxWidth: 220 }}
            />
          </Div>

          <Div
            width="100%"
            row={isDesktop}
            gap={isDesktop ? '12px' : '15px'}
            alignStart
            justifyStart
            wrap={!isDesktop ? 'wrap' : 'nowrap'}
          >
            {LANGUAGES.map((lang) => (
              <Div
                key={lang}
                width={isDesktop ? undefined : '100%'}
                padding="15px"
                borderRadius="8px"
                style={{
                  backgroundColor: '#f5f5f5',
                  flex: isDesktop ? '1 1 0' : undefined,
                  minWidth: isDesktop ? 0 : undefined,
                  boxSizing: 'border-box',
                }}
              >
                <P
                  fontSize="16px"
                  fontWeight="600"
                  margin="0 0 10px 0"
                  textAlign="left"
                >
                  {lang.toUpperCase()}
                </P>
                <Div gap="10px">
                  <Div>
                    <P fontSize="14px" margin="0 0 5px 0" textAlign="left">
                      Title
                    </P>
                    <FeedbackFormInput
                      value={formData.title[lang] || ''}
                      onChange={(e) => patchLang('title', lang, e.target.value)}
                      placeholder={`Title (${lang})`}
                      style={{ padding: '3px 5px', border: '1px gray solid' }}
                    />
                  </Div>
                  <Div>
                    <P fontSize="14px" margin="0 0 5px 0" textAlign="left">
                      Text
                    </P>
                    <P
                      fontSize="12px"
                      margin="0 0 6px 0"
                      textAlign="left"
                      style={{ color: '#666', lineHeight: 1.4 }}
                    >
                      Tags for formatting:{' '}
                      <code>&lt;bold&gt;…&lt;/bold&gt;</code>
                      {', '}
                      <code>&lt;italic&gt;…&lt;/italic&gt;</code>
                      {', '}
                      <code>&lt;underline&gt;…&lt;/underline&gt;</code>
                      . Line breaks become new lines. Example:{' '}
                      <code>&lt;bold&gt;What&apos;s new&lt;/bold&gt;</code>
                    </P>
                    <textarea
                      value={formData.body[lang] || ''}
                      onChange={(e) => patchLang('body', lang, e.target.value)}
                      placeholder={`Release notes (${lang})\n\n<bold>Subtitle</bold>\nDetails…`}
                      rows={8}
                      style={{
                        width: '100%',
                        padding: '8px 5px',
                        border: '1px gray solid',
                        borderRadius: 4,
                        fontFamily: 'inherit',
                        fontSize: 14,
                        boxSizing: 'border-box',
                        resize: 'vertical',
                      }}
                    />
                  </Div>
                </Div>
              </Div>
            ))}
          </Div>

          <Div width="100%" gap="10px" alignStart>
            <P fontWeight="600" align="left" margin="0" textWidth="auto">
              Visibility
            </P>
            <Div row gap="14px" wrap="wrap" justifyStart>
              <label>
                <input
                  type="checkbox"
                  checked={Boolean(formData.webOnly)}
                  onChange={(e) => setVisibilityFlag('webOnly', e.target.checked)}
                />
                {' '}
                Web only
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={Boolean(formData.mobileOnly)}
                  onChange={(e) => setVisibilityFlag('mobileOnly', e.target.checked)}
                />
                {' '}
                Mobile only
              </label>
            </Div>
          </Div>

          <Div row gap="10px" wrap="wrap" justifyStart>
            <Button type="submit" greenButton width="auto" disabled={busy}>
              {saving ? 'Saving…' : isEdit ? 'Update' : 'Create'}
            </Button>
            <Button
              type="button"
              onClick={onCancel}
              width="auto"
              outlineButton
              disabled={busy}
            >
              Cancel
            </Button>
          </Div>
        </Div>
      </form>
    </Div>
  );
}
